import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { fetchAuthSession } from 'aws-amplify/auth';

export default function WoDetails({ item, onClose, editMode, setEditMode }) {
  const [details, setDetails] = useState(item || {});
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const statuses = ['Open', 'Completed'];
  const unitHealthOptions = ['Available', 'Unavailable'];

  useEffect(() => {
    // Normalize incoming dates (MM/DD/YYYY -> YYYY-MM-DD for <input type="date">)
    setDetails({
      ...(item || {}),
      unitNumber: (item?.unitNumber ?? ''),
      blockID: (item?.blockID ?? ''),
      enteredBy: (item?.enteredBy ?? ''),
      woNum: (item?.woNum ?? ''),
      status: (item?.status ?? 'Open'),
      unitHealth: (item?.unitHealth ?? 'Available'),
      Vendor: (item?.Vendor ?? ''),
      description: (item?.description ?? ''),
      dateEntered:
        item && item.dateEntered
          ? moment(item.dateEntered, 'MM/DD/YYYY', true).isValid()
            ? moment(item.dateEntered, 'MM/DD/YYYY').format('YYYY-MM-DD')
            : ''
          : '',
      dateCompleted:
        item && item.dateCompleted
          ? moment(item.dateCompleted, 'MM/DD/YYYY', true).isValid()
            ? moment(item.dateCompleted, 'MM/DD/YYYY').format('YYYY-MM-DD')
            : ''
          : '',
    });
  }, [item]);

  async function authHeaders() {
    const { tokens } = await fetchAuthSession();
    const jwt =
      tokens?.idToken?.toString?.() ??
      tokens?.accessToken?.toString?.();
    return jwt ? { Authorization: `Bearer ${jwt}` } : {};
  }

  const handleSave = async () => {
    setSaving(true);
    setErrorMsg('');

    const url = 'https://2af2e5kjj9.execute-api.us-east-1.amazonaws.com/prod/updateWO';

    // Convert local YYYY-MM-DD inputs -> backend MM/DD/YYYY
    const saveDetails = {
      ...details,
      dateEntered: details.dateEntered
        ? moment(details.dateEntered, 'YYYY-MM-DD', true).isValid()
          ? moment(details.dateEntered, 'YYYY-MM-DD').format('MM/DD/YYYY')
          : ''
        : '',
      dateCompleted: details.dateCompleted
        ? moment(details.dateCompleted, 'YYYY-MM-DD', true).isValid()
          ? moment(details.dateCompleted, 'YYYY-MM-DD').format('MM/DD/YYYY')
          : ''
        : '',
    };

    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(await authHeaders()),
        },
        body: JSON.stringify(saveDetails),
      });

      const text = await res.text();
      let json;
      try { json = text ? JSON.parse(text) : null; } catch { json = null; }

      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          throw new Error('Unauthorized. Please sign in to save work orders.');
        }
        throw new Error(json?.message || `Save failed (HTTP ${res.status}).`);
      }

      onClose();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error during save operation:', err);
      setErrorMsg(err?.message || 'Error during save operation');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-content">
        <button className="modal-close" onClick={onClose}>X</button>

        {errorMsg && (
          <div style={{ color: '#C80036', marginBottom: 8 }}>{errorMsg}</div>
        )}

        <div className="container modal-group">
          <div className="detail-data">
            {editMode ? (
              <div className="details-row">
                <label>Unit Number:</label>
                <input
                  type="text"
                  className="input-style"
                  value={details.unitNumber || ''}
                  onChange={e => setDetails({ ...details, unitNumber: e.target.value })}
                />
              </div>
            ) : (
              <div className="details-row"><strong>Unit Number:</strong> {details.unitNumber}</div>
            )}

            {editMode ? (
              <div className="details-row">
                <label>Block ID:  </label>
                <input
                  type="text"
                  className="input-style"
                  value={details.blockID || ''}
                  onChange={e => setDetails({ ...details, blockID: e.target.value })}
                />
              </div>
            ) : (
              <div className="details-row"><strong>Block ID:</strong> {details.blockID}</div>
            )}

            {editMode ? (
              <div className="details-row">
                <label>Entered By:  </label>
                <input
                  type="text"
                  className="input-style"
                  value={details.enteredBy || ''}
                  onChange={e => setDetails({ ...details, enteredBy: e.target.value })}
                />
              </div>
            ) : (
              <div className="details-row"><strong>Entered By:</strong> {details.enteredBy}</div>
            )}
          </div>

          <div className="detail-data">
            {editMode ? (
              <div className="details-row">
                <label>Work Order:  </label>
                <input
                  type="text"
                  className="input-style"
                  value={details.woNum || ''}
                  onChange={e => setDetails({ ...details, woNum: e.target.value })}
                />
              </div>
            ) : (
              <div className="details-row"><strong>Work Order:</strong> {details.woNum}</div>
            )}

            {editMode ? (
              <div className="details-row">
                <label>Status:</label>
                <select
                  className=" select-style"
                  value={details.status || 'Open'}
                  onChange={e => setDetails({ ...details, status: e.target.value })}
                >
                  {statuses.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="details-row"><strong>Status:</strong> {details.status}</div>
            )}

            {editMode ? (
              <div className="details-row">
                <label>Date Entered:</label>
                <input
                  type="date"
                  className=" select-style"
                  value={details.dateEntered || ''}
                  onChange={e => setDetails({ ...details, dateEntered: e.target.value })}
                />
              </div>
            ) : (
              <div className="details-row"><strong>Date Entered:</strong> {details.dateEntered}</div>
            )}
          </div>

          <div className="detail-data">
            {editMode ? (
              <div className="details-row">
                <label>Unit Health:</label>
                <select
                  className=" select-style"
                  value={details.unitHealth || 'Available'}
                  onChange={e => setDetails({ ...details, unitHealth: e.target.value })}
                >
                  {unitHealthOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="details-row"><strong>Unit Health:</strong> {details.unitHealth}</div>
            )}

            {editMode ? (
              <div className="details-row">
                <label>Vendor:</label>
                <input
                  list="vendors"
                  className=" select-style"
                  value={details.Vendor || ''}
                  onChange={e => setDetails({ ...details, Vendor: e.target.value })}
                />
                <datalist id="vendors">
                  {/* Dynamically populate this list if needed */}
                  <option value="AMERIT"></option>
                  <option value="CEI"></option>
                  <option value="Dealer"></option>
                  <option value="Goodyear"></option>
                  <option value="Velocity"></option>
                </datalist>
              </div>
            ) : (
              <div className="details-row"><strong>Vendor:</strong> {details.Vendor}</div>
            )}

            {editMode ? (
              <div className="details-row">
                <label>Date Completed:</label>
                <input
                  type="date"
                  className=" select-style"
                  value={details.dateCompleted || ''}
                  onChange={e => setDetails({ ...details, dateCompleted: e.target.value })}
                />
              </div>
            ) : (
              <div className="details-row"><strong>Date Completed:</strong> {details.dateCompleted}</div>
            )}
          </div>
        </div>

        <div className="detail=data">
          {editMode ? (
            <div className="details-row">
              <label>Description:</label>
              <textarea
                className="input-style scrollable-textbox"
                value={details.description || ''}
                onChange={e => setDetails({ ...details, description: e.target.value })}
              />
            </div>
          ) : (
            <div className="details-row"><strong>Description:</strong> {details.description}</div>
          )}
        </div>

        <div className="wo-images">
          <strong>Upload Files</strong>
        </div>

        <div className="controls">
          {editMode ? (
            <div>
              <button onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button onClick={onClose} disabled={saving}>Cancel</button>
            </div>
          ) : (
            <button onClick={() => setEditMode(true)}>Edit</button>
          )}
        </div>
      </div>
    </div>
  );
}
